import { Link } from 'react-router-dom';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import { Course } from '../../types/elearning';
import { courseBlurb, courseFace, courseFact, courseStatus } from '../../utils/cardFacts';

interface CourseCardProps {
  course: Course;
  enrollment?: {
    status: string;
    progress: number;
    moduleCompletions?: { moduleId: string }[];
  };
  view?: 'grid' | 'list';
}

function CourseMeter({ progress, done }: { progress: number; done: boolean }) {
  const width = done ? 100 : Math.max(0, Math.min(100, progress));
  return (
    <div className="mt-4 h-2 rounded-full bg-primary/10 overflow-hidden" aria-hidden>
      <div className={`h-full ${done ? 'bg-[#3F6E60]' : 'bg-primary'}`} style={{ width: `${width}%` }} />
    </div>
  );
}

function courseAction(stage: 'open' | 'active' | 'done', title: string, to: string) {
  if (stage === 'done') {
    return <QuietAction to={to} label={`Review course ${title}`}>Review course</QuietAction>;
  }
  if (stage === 'active') {
    return <QuietAction to={to} label={`Continue ${title}`}>Continue</QuietAction>;
  }
  return <QuietAction to={to} label={`View course ${title}`}>View course</QuietAction>;
}

export default function CourseCard({ course, enrollment }: CourseCardProps) {
  const stage = courseFace(course, enrollment);
  const status = courseStatus(stage.stage);
  const blurb = courseBlurb(course);
  const fact = courseFact(course, stage.stage, stage.finished, stage.progress);
  const to = `/elearning/courses/${course.slug}`;

  return (
    <Card className="!p-4 self-start h-auto w-full">
      <p className="flex items-center gap-2 text-sm font-semibold text-primary">
        <AcademicCapIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
        Course
      </p>
      <h3 className="mt-1 text-lg font-semibold text-gray-900">{course.title}</h3>
      {blurb && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{blurb}</p>}
      <p className={`mt-4 text-sm font-semibold ${status.className}`}>{status.label}</p>
      <p className="text-sm text-gray-600">{fact}</p>
      {stage.stage !== 'open' && <CourseMeter progress={stage.progress} done={stage.stage === 'done'} />}
      <div className="mt-4 flex justify-end">{courseAction(stage.stage, course.title, to)}</div>
    </Card>
  );
}

export function CourseRow({
  course,
  enrollment,
}: {
  course: Course;
  enrollment?: CourseCardProps['enrollment'];
}) {
  const face = courseFace(course, enrollment);
  const status = courseStatus(face.stage);
  const blurb = courseBlurb(course);
  const fact = courseFact(course, face.stage, face.finished, face.progress);

  return (
    <Link
      to={`/elearning/courses/${course.slug}`}
      aria-label={course.title}
      className="flex items-start gap-4 w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus-ring"
    >
      <AcademicCapIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${status.className}`}>{status.label}</p>
        <p className="text-base font-semibold text-gray-900">{course.title}</p>
        {blurb && <p className="mt-1 text-sm text-gray-600 line-clamp-1">{blurb}</p>}
        <p className="mt-1 text-sm text-gray-600">{fact}</p>
      </div>
    </Link>
  );
}
